import { useAuthStore } from '../stores/useAuthStore';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Fires once per 401 cascade so we don't call signOut repeatedly when
// multiple in-flight requests fail at once.
//
// Scope is LOCAL on purpose: this path is reached by a request failing, not by
// the user asking to leave. A global sign-out would revoke the refresh token on
// every other device over what may be one bad response. The logout button still
// signs out globally (see useAuthStore.signOut).
let unauthorizedInFlight = false;
export async function handleUnauthorized(): Promise<void> {
  if (unauthorizedInFlight) return;
  unauthorizedInFlight = true;
  try {
    await useAuthStore.getState().signOut('local');
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  } finally {
    unauthorizedInFlight = false;
  }
}

/** Renew the access token via Supabase. Returns the NEW token, or null when the
 *  session genuinely cannot be renewed. Concurrent callers share one refresh so
 *  a burst of 401s (several requests in flight) triggers a single round-trip. */
let refreshInFlight: Promise<string | null> | null = null;
export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    const before = useAuthStore.getState().token;
    refreshInFlight = useAuthStore
      .getState()
      .refreshSession()
      .then(() => {
        const after = useAuthStore.getState().token;
        return after && after !== before ? after : null;
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/** `fetch` with the bearer token attached and ONE silent refresh-and-retry on 401.
 *
 *  Every 401 used to end the session immediately. During an audit the page talks
 *  to the API continuously, so a token that rotated a moment ago — or a single
 *  hiccup on the backend's auth path (the project signs tokens with ES256, and
 *  verification there depends on a JWKS lookup with a network fallback) — logged
 *  the user out mid-run. The session is now abandoned only when Supabase itself
 *  refuses to renew it.
 *
 *  Returns the response as-is, 401 included, so callers keep their own error
 *  handling; `handleUnauthorized()` has already run by then. */
export async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const send = (token?: string) =>
    fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        ...(init?.headers as Record<string, string> | undefined),
        ...(token ? { Authorization: `Bearer ${token}` } : authHeaders()),
      },
    });

  const res = await send();
  if (res.status !== 401) return res;

  const fresh = await refreshAccessToken();
  if (!fresh) {
    await handleUnauthorized();
    return res;
  }

  const retried = await send(fresh);
  if (retried.status === 401) await handleUnauthorized();
  return retried;
}

/** A failed API call, carrying the server's own explanation.
 *
 *  FastAPI answers errors with `{"detail": "…"}`. We used to throw before ever
 *  reading the body, so every backend message was discarded and callers could
 *  only show a generic string — a Shopify claim that failed because nothing was
 *  parked for that store looked exactly like a database outage. `detail` is the
 *  server's message when there was one; `message` falls back to the status line.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  /** Machine-readable code from a structured `detail` object, when the server
   *  sent one (e.g. `meta_pages_not_granted`). Absent for a plain string detail. */
  readonly code?: string;

  constructor(status: number, statusText: string, detail?: string, code?: string) {
    super(detail || `API error: ${status} ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.code = code;
  }
}

/** Pull the server's message out of an error body, tolerating any shape.
 *
 *  FastAPI's `detail` is usually a string, but a route that needs the caller to
 *  BRANCH on the failure sends an object instead — `{error, message}`, as Meta's
 *  save does for `meta_pages_not_granted` / `meta_pages_unlinkable` (VIBE-1916).
 *  Reading only the string form turned those into a bare "API error: 400 Bad
 *  Request": the server's explanation was on the wire and thrown away. */
async function errorDetail(res: Response): Promise<{ detail?: string; code?: string }> {
  try {
    const body: unknown = await res.clone().json();
    if (body && typeof body === 'object') {
      // `detail` is FastAPI's field; `error` is what our Next.js routes use.
      const d = (body as { detail?: unknown; error?: unknown }).detail
        ?? (body as { error?: unknown }).error;
      if (typeof d === 'string' && d.trim()) return { detail: d };
      if (d && typeof d === 'object') {
        const obj = d as { error?: unknown; message?: unknown; detail?: unknown };
        const message = [obj.message, obj.detail, obj.error]
          .find((v): v is string => typeof v === 'string' && v.trim().length > 0);
        const code = typeof obj.error === 'string' && obj.error.trim() ? obj.error : undefined;
        if (message || code) return { detail: message, code };
      }
    }
  } catch {
    // Non-JSON body (an HTML 404 from a proxy, an empty response) — no detail.
  }
  return {};
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await authedFetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (res.status === 401) {
    const { detail, code } = await errorDetail(res);
    throw new ApiError(401, 'Unauthorized', detail, code);
  }

  if (!res.ok) {
    const { detail, code } = await errorDetail(res);
    throw new ApiError(res.status, res.statusText, detail, code);
  }

  return res.json();
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function apiDelete(path: string): Promise<void> {
  const res = await authedFetch(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  if (res.status === 401) {
    throw new Error('API error: 401 Unauthorized');
  }
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
}

/** Server-side runtime config (`GET /api/config`), read from the backend's own
 *  environment on every request.
 *
 *  Deliberately NOT `import.meta.env.VITE_*`: this SPA is compiled during the
 *  Docker build and served as static files, so a VITE_ value can only change
 *  with a rebuild — i.e. a deploy. A flag that has to be switchable by
 *  restarting the API container must come from the API (VIBE-1786).
 */
export interface RuntimeConfig {
  /** Whether the Shopify connector is offered in the connect grid. */
  shopify_integration_enabled: boolean;
}

/** Fetch the runtime config. Unauthenticated on purpose: the endpoint is public
 *  and this runs on app load, possibly before sign-in — going through
 *  `authedFetch` would feed a 401 into the sign-out cascade. */
export async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  const res = await fetch(`${BASE_URL}/api/config`);
  if (!res.ok) {
    const { detail, code } = await errorDetail(res);
    throw new ApiError(res.status, res.statusText, detail, code);
  }
  return res.json();
}

/** Absolute URL of `GET /api/shopify/start`, which redirects to this
 *  environment's Shopify install link — read from the backend's own environment
 *  on every request, for the same reason as `/api/config`.
 *
 *  A top-level navigation target, not a fetch: nothing to authenticate, so it
 *  never goes through `authedFetch`. Built on `BASE_URL` like every other API
 *  call, because in local dev the backend runs on a different origin from Vite. */
export function shopifyStartUrl(): string {
  return `${BASE_URL}/api/shopify/start`;
}

export async function createConversation(title?: string, signal?: AbortSignal): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/api/conversations', {
    method: 'POST',
    body: JSON.stringify({ title }),
    signal,
  });
}

export async function fetchAuditReport(chatId: string): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>(`/api/audit/${chatId}/report`);
}

export async function fetchLatestAnalysisChatId(): Promise<string | null> {
  try {
    const data = await apiFetch<{ chat_id: string | null }>('/api/audit/latest/chat-id');
    return data.chat_id;
  } catch {
    return null;
  }
}

export type AnalysisStatus =
  | 'idle'
  | 'running'
  | 'completed'
  | 'failed'
  | 'crashed'
  | 'recovering';

export interface AuditStatus {
  chat_id: string;
  tool_count: number;
  tools_completed: string[];
  is_complete: boolean;
  has_deliverables: boolean;
  /**
   * True once the renderable dashboard block (`compiled_report`) is saved.
   * Use this to route users to `/report` (with the review placeholder) — even
   * if PDF/PPTX files haven't been emitted yet or refine got stuck.
   */
  has_renderable_report?: boolean;
  current_stage: number;
  /** Total stages in the LATEST pass (7 for analysis, 4 for refine, 0 if no pass yet). */
  current_total_stages?: number;
  is_running: boolean;
  has_error: boolean;
  /** Machine-readable failure reason (e.g. 'no_data') when has_error; else null. */
  error_reason?: string | null;
  last_seq: number;
  counters: { dataPoints: number; insights: number; recommendations: number };
  /** Lifecycle status written by the backend heartbeat/recovery path. */
  analysis_status?: AnalysisStatus;
}

export async function fetchAuditStatus(chatId: string): Promise<AuditStatus> {
  return apiFetch(`/api/audit/${chatId}/status`);
}

export interface AnalysisEvent {
  seq: number;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export async function fetchAnalysisEvents(
  chatId: string,
  afterSeq = 0,
): Promise<{ events: AnalysisEvent[]; chat_id: string }> {
  return apiFetch(`/api/audit/${chatId}/events?after_seq=${afterSeq}`);
}

export async function fetchCentrifugoToken(): Promise<{ token: string; ws_url: string }> {
  return apiFetch('/api/centrifugo/token');
}

export async function cancelAnalysis(chatId: string): Promise<{ status: string }> {
  return apiPost(`/api/chat/${chatId}/cancel`, {});
}

/**
 * Resolve a pending human-in-the-loop tool approval for a running analysis
 * (APPROVAL_MODE=selective|always). Unblocks the paused agent loop server-side.
 */
export async function approveToolCall(chatId: string, approved: boolean): Promise<{ status: string }> {
  return apiPost(`/api/chat/${chatId}/approve`, { approved });
}

export async function startAnalysis(
  chatId: string,
  message: string,
  profile: string,
  context?: Record<string, string>,
): Promise<{ status: string; chat_id: string }> {
  return apiPost('/api/chat/start', { chat_id: chatId, message, profile, ...(context ? { context } : {}) });
}

/**
 * Launch the full owner audit pipeline via the server-set profile route
 * (`audit-supervisor-owner`). One call auto-chains analysis → client-facing
 * refine → compiled_report → scoring, streaming on Centrifugo `analysis:{chatId}`.
 * The profile is set SERVER-SIDE by this owner route, so we never send one.
 */
export async function startOwnerAudit(
  chatId: string,
  context?: Record<string, string>,
): Promise<{ status: string; chat_id: string }> {
  const message = 'Run a full business audit.';
  return apiPost('/api/owner/audit/start', { chat_id: chatId, message, ...(context ? { context } : {}) });
}

export async function uploadFile(
  file: File,
  chatId?: string,
): Promise<{ file_id: string; filename: string; mime_type: string; size: number; chat_id: string | null }> {
  const formData = new FormData();
  formData.append('file', file);
  if (chatId) formData.append('chat_id', chatId);
  const res = await authedFetch('/api/files/upload', {
    method: 'POST',
    body: formData,
  });
  if (res.status === 401) {
    throw new Error('Upload failed: 401 Unauthorized');
  }
  if (!res.ok) {
    // Surface backend-provided detail (415 magic-byte mismatch, 422 AV block, etc.)
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* empty body */ }
    throw new Error(`Upload failed: ${res.status}${detail ? ` — ${detail}` : ''}`);
  }
  return res.json();
}

/** Thrown by saveCredentials when the backend rejects creds at validation
 * (HTTP 422 from PUT /api/credentials). Carries the backend's structured
 * detail so the form can render a precise hint (e.g. "available_projects"). */
export class CredentialValidationError extends Error {
  readonly provider: string;
  readonly errorCode: string;
  readonly detail: Record<string, unknown>;
  constructor(provider: string, errorCode: string, message: string, detail: Record<string, unknown>) {
    super(message);
    this.name = 'CredentialValidationError';
    this.provider = provider;
    this.errorCode = errorCode;
    this.detail = detail;
  }
}

export async function saveCredentials(provider: string, credentials: Record<string, unknown>): Promise<void> {
  const res = await authedFetch('/api/credentials', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, credentials }),
  });
  if (res.status === 401) {
    throw new Error('API error: 401 Unauthorized');
  }
  if (res.status === 422) {
    // Backend validator (src/services/credentials/) rejected the creds.
    // The detail is structured: { provider, error_code, message, ... }.
    let parsed: unknown = null;
    try { parsed = (await res.json())?.detail; } catch { /* empty body */ }
    const d = (parsed && typeof parsed === 'object') ? parsed as Record<string, unknown> : {};
    throw new CredentialValidationError(
      typeof d.provider === 'string' ? d.provider : provider,
      typeof d.error_code === 'string' ? d.error_code : 'invalid',
      typeof d.message === 'string' ? d.message : 'Incorrect credentials',
      d,
    );
  }
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
}

export async function deleteCredentials(provider: string): Promise<void> {
  await apiDelete(`/api/credentials/${provider}`);
}

/** Disconnect every platform and delete everything collected from them.
 *  Reports, chats and the signed agreement are deliberately left intact. */
export async function deleteAllConnectedData(): Promise<void> {
  await apiDelete('/api/credentials');
}

// AppsFlyer app picker (POST /api/credentials/appsflyer/apps)

/** One app from AppsFlyer's Management API, normalized by the backend. */
export interface AppsflyerApp {
  id: string;
  name: string | null;
  platform: string | null;
  currency: string | null;
  time_zone: string | null;
}

/** List the AppsFlyer apps available to a pasted V2.0 token so the connect
 * modal can offer a picker instead of manual app-ID entry. Throws
 * CredentialValidationError on 422 (invalid token — keep the user on the
 * token step); any other failure is a plain Error (callers fall back to
 * manual entry). */
export async function fetchAppsflyerApps(bearerToken: string): Promise<{ apps: AppsflyerApp[] }> {
  const res = await authedFetch('/api/credentials/appsflyer/apps', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bearer_token: bearerToken }),
  });
  if (res.status === 401) {
    throw new Error('API error: 401 Unauthorized');
  }
  if (res.status === 422) {
    let parsed: unknown = null;
    try { parsed = (await res.json())?.detail; } catch { /* empty body */ }
    const d = (parsed && typeof parsed === 'object') ? parsed as Record<string, unknown> : {};
    throw new CredentialValidationError(
      'appsflyer',
      typeof d.error_code === 'string' ? d.error_code : 'invalid_token',
      typeof d.message === 'string' ? d.message : 'Bearer token is invalid or lacks access.',
      d,
    );
  }
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export interface AppleSearchAdsOrg {
  org_id: string;
  org_name: string | null;
  currency: string | null;
  time_zone: string | null;
  parent_org_id: string | null;
  payment_model: string | null;
  role_names: string[];
}

export interface AppleSearchAdsCertificate {
  client_id: string;
  team_id: string;
  key_id: string;
  private_key: string;
}

/**
 * List the Campaign Groups an Apple Ads certificate can reach.
 *
 * Apple Search Ads has no redirect OAuth — the advertiser uploads a public key
 * in the Apple Ads UI and pastes the matching private key here. Nothing is
 * stored by this call; the user picks orgs first, then saves.
 */
export async function fetchAppleSearchAdsOrgs(
  cert: AppleSearchAdsCertificate,
): Promise<{ orgs: AppleSearchAdsOrg[] }> {
  const res = await authedFetch('/api/credentials/apple-search-ads/orgs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cert),
  });
  if (res.status === 401) {
    throw new Error('API error: 401 Unauthorized');
  }
  if (res.status === 422) {
    let parsed: unknown = null;
    try { parsed = (await res.json())?.detail; } catch { /* empty body */ }
    const d = (parsed && typeof parsed === 'object') ? parsed as Record<string, unknown> : {};
    throw new CredentialValidationError(
      'apple_search_ads',
      typeof d.error_code === 'string' ? d.error_code : 'invalid_certificate',
      typeof d.message === 'string' ? d.message : 'Apple rejected the certificate.',
      d,
    );
  }
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function loadCredentials(): Promise<Record<string, string[]>> {
  return apiFetch<Record<string, string[]>>('/api/credentials');
}

// TikTok OAuth
export async function getTikTokAuthUrl(): Promise<{ url: string }> {
  return apiPost<{ url: string }>('/api/tiktok/auth-url', {});
}

// Shopify install is Shopify-initiated (App Store req 2.3.1): the merchant installs
// from the App Store listing, the backend parks the store, then redirects here with
// ?shopify=claim&shop=. This links the parked store to the signed-in account.
export async function claimShopifyInstall(
  shop: string,
): Promise<{ status?: string; shop_domain?: string; error?: string }> {
  return apiPost<{ status?: string; shop_domain?: string; error?: string }>(
    '/api/shopify/claim',
    { shop_domain: shop },
  );
}

/** One connected Shopify store (multiple stores can hang off one project). */
export interface ShopifyShop {
  shop_domain: string;
  shop_name: string | null;
  primary_domain: string | null;
  status: string;
  currency: string | null;
  plan_name: string | null;
}

export async function listShopifyShops(): Promise<ShopifyShop[]> {
  const res = await apiFetch<{ shops: ShopifyShop[] }>('/api/shopify/shops');
  return res.shops || [];
}

export async function disconnectShopifyShop(shopDomain: string): Promise<void> {
  await apiDelete(`/api/shopify/shops/${encodeURIComponent(shopDomain)}`);
}

/** The Confirm form, in backend field names (VIBE-2035). */
export interface ConfirmedProfile {
  name: string;
  website_url: string;
  description: string;
  business_model: string;
  revenue_models: string[];
  challenges: string[];
  business_stage: string;
  revenue_range: string;
  success_goal: string;
}

export async function saveQuestionnaire(
  chatId: string,
  answers: Record<string, string | string[]>,
  profile?: ConfirmedProfile,
): Promise<void> {
  await apiPost('/api/questionnaire', { chat_id: chatId, answers, ...(profile ? { profile } : {}) });
}

// Report history
export interface ReportHistoryItem {
  chat_id: string;
  project_name: string;
  created_at: string;
  file_count: number;
}

export async function fetchReportHistory(): Promise<ReportHistoryItem[]> {
  const res = await apiFetch<{ reports: ReportHistoryItem[] }>('/api/reports/history');
  return res.reports || [];
}

export interface ProjectInfo {
  name: string;
  description: string;
  website_url: string;
  email: string;
  logo_path: string | null;
  product_types: string[];
  business_model: string;
  revenue_models: string[];
}

export async function fetchProjectInfo(): Promise<ProjectInfo | null> {
  const res = await apiFetch<{ data: ProjectInfo | null }>('/api/project-info');
  return res.data;
}

export async function saveProjectInfo(data: ProjectInfo): Promise<void> {
  await apiPut('/api/project-info', data);
}

export async function fetchQuestions(): Promise<Array<{ id: string; question: string; type: string; options?: string[] }>> {
  return apiFetch('/api/questionnaire/questions');
}

/** What the Confirm step shows: the business described back from the data the
 *  Collect pass fetched. `business_stage` / `revenue_range` / `success_goal`
 *  are inferred but never rendered — they're saved straight to the
 *  questionnaire, which the strategy agent and report compiler read. */
export interface ProfileSuggestion {
  name: string;
  website_url: string;
  description: string;
  business_model: string;
  revenue_models: string[];
  challenges: string[];
  business_stage: string;
  revenue_range: string;
  success_goal: string;
  _sources: string[];
  _reasoning: string;
  /** Connected billing systems that show how the business is paid. */
  _revenue_evidence?: string[];
  /** The platform the revenue model was set from outright (a Shopify store), else empty. */
  _revenue_set_from?: string;
  /** True when this is what the user already confirmed for the chat. */
  _confirmed?: boolean;
}

export async function suggestProjectInfo(chatId: string | null): Promise<ProfileSuggestion> {
  return apiPost('/api/project-info/suggest', { chat_id: chatId });
}

// ─── Signed agreements (onboarding "Sign" step) ───────────────────────────

/** A paragraph or bullet list. Paragraph text carries `{{merge}}` fields the
 *  client fills in live as the signer types — no HTML crosses the wire. */
export type NdaBlock =
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] };

export interface NdaSection {
  heading: string;
  blocks: NdaBlock[];
}

export interface NdaDocument {
  document_type: string;
  version: string;
  date: string;
  template: { title: string; sections: NdaSection[] };
}

export interface AgreementStatus {
  signed: boolean;
  signed_at: string | null;
  document_version: string | null;
  current_version: string;
  /** Null until the user has signed once. */
  agreement_id: string | null;
  /** Whether a downloadable PDF exists for that signature. */
  has_document: boolean;
  signer_email: string | null;
}

export interface SignAgreementPayload {
  company: string;
  signer_name: string;
  signer_title: string;
  signer_email: string;
  signature_kind: 'drawn' | 'uploaded';
  /** data: URL of the mark. Both modes produce one, so it is always sent. */
  signature_data: string;
  consents: { esign: boolean; privacy: boolean; terms: boolean };
}

export async function fetchNdaDocument(): Promise<NdaDocument> {
  return apiFetch('/api/agreements/nda');
}

export async function fetchAgreementStatus(): Promise<AgreementStatus> {
  return apiFetch('/api/agreements/status');
}

export async function signAgreement(
  payload: SignAgreementPayload,
): Promise<{
  status: string;
  agreement_id: string;
  signed_at: string;
  document_hash: string;
  has_document: boolean;
}> {
  return apiPost('/api/agreements/sign', payload);
}


// ─── Google Analytics OAuth ───────────────────────────────────────────────

export interface GAProperty {
  property_id: string;
  property_name: string;
  property_type?: string;
}

export interface GAAccount {
  account_id: string;
  account_name: string;
  properties: GAProperty[];
}

export async function startGAOAuth(): Promise<{ auth_url: string }> {
  return apiPost<{ auth_url: string }>('/api/oauth/google-analytics/start', {});
}

export async function fetchGAAccounts(sessionId: string): Promise<{ accounts: GAAccount[] }> {
  return apiFetch<{ accounts: GAAccount[] }>(
    `/api/oauth/google-analytics/accounts?session_id=${encodeURIComponent(sessionId)}`,
  );
}

export interface SelectedGAProperty {
  property_id: string;
  property_name: string;
  account_name: string;
}

export interface SaveGAPropertiesPayload {
  session_id: string;
  properties: SelectedGAProperty[];
}

export async function saveGAProperties(
  payload: SaveGAPropertiesPayload,
): Promise<{ status: string; count: number }> {
  return apiPost('/api/oauth/google-analytics/save', payload);
}

// ── Google Ads OAuth ────────────────────────────────────────────
export interface GoogleAdsCustomer {
  customerId: string;
  customerName: string;
  isManager: boolean;
  accountStatus: string;
  parentCustomerId: string | null;
  currencyCode?: string | null;
  timeZone?: string | null;
  children?: GoogleAdsCustomer[];
}

export interface GoogleAdsCustomersResponse {
  googleEmail: string | null;
  hierarchy: GoogleAdsCustomer[];
  customers: GoogleAdsCustomer[];
}

export interface MerchantAccount {
  accountId: string;
  accountName: string;
}

export interface MerchantAccountsResponse {
  accounts: MerchantAccount[];
  scopeError?: boolean;
  message?: string;
}

export interface SelectedGoogleAdsCustomer {
  customer_id: string;
  customer_name?: string | null;
  is_manager?: boolean;
  parent_customer_id?: string | null;
  is_managed?: boolean;
  login_customer_id?: string | null;
  login_customer_name?: string | null;
  merchant_center_id?: string | null;
  currency_code?: string | null;
  time_zone?: string | null;
}

export interface SaveGoogleAdsCustomersPayload {
  session_id: string;
  selected_customers: SelectedGoogleAdsCustomer[];
}

export async function startGoogleAdsOAuth(): Promise<{ auth_url: string }> {
  return apiPost<{ auth_url: string }>('/api/oauth/google-ads/start', {});
}

export async function fetchGoogleAdsCustomers(
  sessionId: string,
): Promise<GoogleAdsCustomersResponse> {
  return apiFetch<GoogleAdsCustomersResponse>(
    `/api/oauth/google-ads/customers?session_id=${encodeURIComponent(sessionId)}`,
  );
}

export async function fetchGoogleAdsMerchantAccounts(
  sessionId: string,
): Promise<MerchantAccountsResponse> {
  return apiFetch<MerchantAccountsResponse>(
    `/api/oauth/google-ads/merchant-accounts?session_id=${encodeURIComponent(sessionId)}`,
  );
}

export async function saveGoogleAdsCustomers(
  payload: SaveGoogleAdsCustomersPayload,
): Promise<{ status: string; count: number }> {
  return apiPost('/api/oauth/google-ads/save', payload);
}

// ── LinkedIn Ads OAuth ──────────────────────────────────────────
export interface LinkedInAdAccount {
  ad_account_urn: string;
  ad_account_id: string;
  account_name: string;
  account_type: string | null;
  account_status: string | null;
  organization_urn: string | null;
  currency: string | null;
  time_zone: string | null;
}

export interface LinkedInAccountsResponse {
  memberEmail: string | null;
  accounts: LinkedInAdAccount[];
}

export interface SaveLinkedInAccountsPayload {
  session_id: string;
  selected_accounts: LinkedInAdAccount[];
}

export async function startLinkedInOAuth(): Promise<{ auth_url: string }> {
  return apiPost<{ auth_url: string }>('/api/oauth/linkedin/start', {});
}

export async function fetchLinkedInAccounts(
  sessionId: string,
): Promise<LinkedInAccountsResponse> {
  return apiFetch<LinkedInAccountsResponse>(
    `/api/oauth/linkedin/accounts?session_id=${encodeURIComponent(sessionId)}`,
  );
}

export async function saveLinkedInAccounts(
  payload: SaveLinkedInAccountsPayload,
): Promise<{ status: string; count: number }> {
  return apiPost('/api/oauth/linkedin/save', payload);
}

/** Probe the stored LinkedIn token. Access tokens live ~60 days and the app
 * gets no refresh token unless it is an approved MDP partner, so a connection
 * can die silently — the Connect view checks once on mount. */
export async function checkLinkedInTokenHealth(): Promise<{
  connected: boolean; valid: boolean;
}> {
  return apiPost('/api/oauth/linkedin/token-health', {});
}

// ── Meta / Facebook OAuth ───────────────────────────────────────
export interface MetaAdAccount {
  id: string;
  name: string;
  currency?: string;
  timezone_name?: string;
  account_status?: number;
}

/** A Facebook Page as the picker sees it (VIBE-1916).
 *
 *  Every listed Page is selectable. `/pages` also sends `has_token` (whether Meta
 *  issued a page token), but only as a diagnostic: it is deliberately absent from
 *  this type so no rule can be built on it — the audit profiles a Page with the
 *  user token and never reads the stored page token.
 *
 *  `linked` is NOT interchangeable with the response's `linked_page_ids`: it is
 *  false on EVERY row when the baseline could not be read, so a rule built on it
 *  silently degrades to "nothing is linked". It seeds the checkboxes;
 *  `linked_page_ids` decides what an empty selection means.
 */
export interface MetaPage {
  id: string;
  /** Meta may answer without a name; the picker falls back to the id. */
  name: string | null;
  category?: string | null;
  /** Seed for the checkbox only — see the note above. */
  linked: boolean;
}

export interface MetaPagesResponse {
  pages: MetaPage[];
  /** False → `pages` is always empty and no Graph call was made (not an error).
   *  Offer the second, on-demand Page grant instead. */
  pages_scope_granted: boolean;
  /** The Pages currently linked for this user, or `null` when that could not be
   *  determined. `null` means the baseline is UNKNOWN: an empty selection is
   *  then not a deliberate clear and the clear must not be offered at all. */
  linked_page_ids: string[] | null;
  /** Linked Pages absent from `pages`. Non-empty →
   *  the linked set cannot be reproduced here, so — again — an empty selection
   *  is not a clear. */
  unavailable_linked_page_ids: string[];
}

/** An Instagram account as the Instagram step sees it (VIBE-1990).
 *
 *  Only accounts the user can advertise with are listed: through a Business
 *  Manager asset they are assigned to, an ad account they can advertise on, or —
 *  when the connection carries the Page permission — a Page they can advertise
 *  on; so a Page is NOT required. `business_*` / `linked_page_*` are provenance
 *  for display only.
 *
 *  `linked` is the per-row echo of `linked_instagram_ids` and nothing more: it is
 *  false on EVERY row when the baseline could not be read. The step seeds from
 *  `linked_instagram_ids`, which is the only field that can say "unknown".
 */
export interface MetaInstagramAccount {
  id: string;
  /** Meta may answer without one; the picker falls back to the id. */
  username: string | null;
  name: string | null;
  business_id: string | null;
  business_name: string | null;
  linked_page_id: string | null;
  linked_page_name: string | null;
  linked: boolean;
}

export interface MetaInstagramAccountsResponse {
  instagram_accounts: MetaInstagramAccount[];
  /** False → `instagram_accounts` is always empty and no Graph call was made (not
   *  an error, and NOT "you have none" — Meta answers both with the same empty
   *  list). Offer the Instagram grant instead. */
  instagram_scope_granted: boolean;
  /** Every discovery leg finished cleanly. Informational ONLY: it drives the
   *  neutral "may be incomplete" notice and never the clear rule. Always false
   *  without the scope. */
  complete: boolean;
  /** The Instagram accounts currently linked, or `null` when that could not be
   *  determined — the baseline is then UNKNOWN and an empty selection is not a
   *  deliberate clear. */
  linked_instagram_ids: string[] | null;
  /** Linked accounts absent from `instagram_accounts` (every linked one, without
   *  the scope). Non-empty → the linked set cannot be reproduced here: no clear,
   *  and a replace unlinks them. */
  unavailable_linked_instagram_ids: string[];
}

export interface MetaPixel {
  id: string;
  name: string;
}

export interface SelectedMetaPixel {
  pixel_id: string;
  pixel_name: string;
  ad_account_id: string;
}

export interface SaveMetaPayload {
  session_id: string;
  /** Still mandatory — the server answers 400 on an empty list. */
  selected_ad_account_ids: string[];
  /** Three-valued, and the three values are NOT interchangeable:
   *    omitted / null → the step was skipped; existing Page links are untouched
   *    []             → a deliberate clear; every Page link is removed
   *    [...]          → replace the links with exactly these
   *  A skip must OMIT the key. Sending `[]` for a skip erases links the user
   *  never touched — including a selection made in the mvp wizard, which writes
   *  the same rows. */
  selected_page_ids?: string[] | null;
  selected_pixels: SelectedMetaPixel[];
  /** Three-valued exactly like `selected_page_ids` (VIBE-1990):
   *    omitted / null → the step was skipped; existing Instagram links untouched
   *    []             → a deliberate clear; every Instagram link is removed
   *    [...]          → replace the links with exactly these (the server keeps
   *                     only the ones its own discovery resolves)
   *  A skip must OMIT the key — `[]` erases links the user never touched,
   *  including a selection made in the mvp wizard, which writes the same rows. */
  selected_instagram_account_ids?: string[] | null;
}

/** What the save did to the Page links — the server's own account of it. */
export type MetaPagesAction = 'untouched' | 'cleared' | 'replaced';

/** What the save did to the Instagram links — the same vocabulary and rule as
 *  `MetaPagesAction`. */
export type MetaInstagramAction = 'untouched' | 'cleared' | 'replaced';

export interface SaveMetaResult {
  status: string;
  count: number;
  /** NULL when the step was skipped: nothing was counted because nothing was
   *  written, which a 0 would misreport as "you now have no Pages". Guard this
   *  anywhere it is rendered as a number. */
  pages_count: number | null;
  pages_action: MetaPagesAction;
  pixels_count: number;
  /** NULL when the Instagram step was skipped — the same guard as `pages_count`. */
  instagram_count: number | null;
  instagram_action: MetaInstagramAction;
}

/** Structured `detail.error` codes the save can answer with (VIBE-1916, VIBE-1990). */
export const META_SAVE_ERRORS = {
  /** Pages were selected but the connection carries no Page permission. */
  pagesNotGranted: 'meta_pages_not_granted',
  /** None of the selected Pages resolved at save — none could be found for this
   *  connection. Refusing is deliberate: an empty list would read as a clear
   *  downstream. */
  pagesUnlinkable: 'meta_pages_unlinkable',
  /** Instagram accounts were selected but the connection carries no Instagram
   *  permission. */
  instagramNotGranted: 'meta_instagram_not_granted',
  /** None of the selected Instagram accounts could be found for this connection.
   *  Refused for the same reason as `pagesUnlinkable`: writing the empty
   *  resolution would read as a clear. */
  instagramUnlinkable: 'meta_instagram_unlinkable',
} as const;

export async function startMetaOAuth(): Promise<{ auth_url: string }> {
  return apiPost<{ auth_url: string }>('/api/oauth/meta/start', {});
}

export async function fetchMetaAdAccounts(
  sessionId: string,
): Promise<{ ad_accounts: MetaAdAccount[] }> {
  return apiFetch<{ ad_accounts: MetaAdAccount[] }>(
    `/api/oauth/meta/ad-accounts?session_id=${encodeURIComponent(sessionId)}`,
  );
}

export async function fetchMetaPages(
  sessionId: string,
): Promise<MetaPagesResponse> {
  return apiFetch<MetaPagesResponse>(
    `/api/oauth/meta/pages?session_id=${encodeURIComponent(sessionId)}`,
  );
}

/** Authorization URL for the SECOND, on-demand Page grant (VIBE-1916).
 *
 *  Navigate the browser to it exactly as with `/start`. The session is validated
 *  server-side HERE, so a dead one fails (404 gone / 410 expired / 403 not
 *  yours / 503 not configured) before the user spends a trip through Meta's
 *  dialog. The callback returns to the same wizard with the SAME session id. */
export async function grantMetaPages(sessionId: string): Promise<{ auth_url: string }> {
  return apiPost<{ auth_url: string }>('/api/oauth/meta/grant-pages', {
    session_id: sessionId,
  });
}

/** The Instagram step's listing (VIBE-1990). Graph failures never raise here —
 *  they arrive as `complete: false` — so a thrown error is about the SESSION
 *  (404 gone / 410 expired / 403 not yours / 401) or the transport. */
export async function fetchMetaInstagramAccounts(
  sessionId: string,
): Promise<MetaInstagramAccountsResponse> {
  return apiFetch<MetaInstagramAccountsResponse>(
    `/api/oauth/meta/instagram-accounts?session_id=${encodeURIComponent(sessionId)}`,
  );
}

/** Authorization URL for the Instagram step's own grant — the base permissions
 *  plus `instagram_basic`, no Page permission (VIBE-1990).
 *
 *  Navigate the browser to it exactly as with `/grant-pages`, and for the same
 *  reason the session is validated server-side HERE (404 / 410 / 403 / 503)
 *  before the user spends a trip through Meta's dialog. The callback returns to
 *  the same wizard with the SAME session id and reports no grant outcome of its
 *  own: the wizard's draft plus `fetchMetaInstagramAccounts` are the answer. */
export async function grantMetaInstagram(sessionId: string): Promise<{ auth_url: string }> {
  return apiPost<{ auth_url: string }>('/api/oauth/meta/grant-instagram', {
    session_id: sessionId,
  });
}

export async function fetchMetaPixels(
  sessionId: string,
  adAccountId: string,
): Promise<{ pixels: MetaPixel[] }> {
  return apiFetch<{ pixels: MetaPixel[] }>(
    `/api/oauth/meta/pixels?session_id=${encodeURIComponent(sessionId)}&ad_account_id=${encodeURIComponent(adAccountId)}`,
  );
}

export async function saveMetaSelections(
  payload: SaveMetaPayload,
): Promise<SaveMetaResult> {
  return apiPost<SaveMetaResult>('/api/oauth/meta/save', payload);
}
